import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';
import * as analytics from '@/lib/analytics';
import { Modal, Button, Text, ScrollArea, Group, Image, ActionIcon, Stack, Divider } from '@mantine/core';
import { IconShoppingCart, IconX, IconMinus, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

export const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, getTotal, clearCart, justAdded } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (justAdded && items.length > 0) {
      const lastItem = items[items.length - 1];
      notifications.show({
        title: t('cart.addedToCart'),
        message: t('cart.addedToCartMessage', { name: lastItem.product.name }),
        color: 'green',
        icon: <IconShoppingCart size={16} />,
        autoClose: 3000,
      });
    }
  }, [justAdded, items, t]);

  const handleCheckout = () => {
    if (items.length > 0) {
      navigate('/checkout');
      setOpened(false);
    }
  };
  
  // Track cart view when modal opens
  useEffect(() => {
    if (opened && items.length > 0) {
      const gaItems = items.map(item => ({
        item_id: item.product.id,
        item_name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
      }));
      
      analytics.viewCart(gaItems, getTotal());
    }
  }, [opened, items, getTotal]);

  const itemCount = isHydrated ? items.reduce((sum, item) => sum + item.quantity, 0) : 0;

  return (
    <>
      <Button
        leftSection={<IconShoppingCart size={16} />}
        onClick={() => setOpened(true)}
        style={{
          transition: 'all 0.3s ease',
          transform: justAdded ? 'scale(1.1)' : 'scale(1)',
          boxShadow: justAdded ? '0 0 20px rgba(34, 139, 230, 0.5)' : 'none',
        }}
      >
        {t('cart.cart')} ({isHydrated ? itemCount : 0})
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('cart.shoppingCart')}
        size="lg"
      >
        {items.length === 0 ? (
          <Text ta="center" py="xl" c="dimmed">
            {t('cart.empty')}
          </Text>
        ) : (
          <Stack gap="md">
            <ScrollArea h={400}>
              {items.map(item => (
                <div key={item.product.id}>
                  <Group gap="md" align="center" wrap="nowrap">
                    <div style={{ width: '50px', height: '50px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px' }}>
                      <Image
                        src={item.product.images && item.product.images.length > 0 ? item.product.images[0] : undefined}
                        alt={item.product.name}
                        w={50}
                        h={50}
                        fit="cover"
                        radius="sm"
                        fallbackSrc="https://via.placeholder.com/50"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} size="sm" lineClamp={1}>{item.product.name}</Text>
                      <Text size="sm" c="dimmed" mb="xs">
                        {(item.product.price * item.quantity).toFixed(2)} {t('common.pln')}
                      </Text>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        >
                          <IconMinus size={14} />
                        </ActionIcon>
                        <Text size="sm" fw={600} style={{ minWidth: '30px', textAlign: 'center' }}>
                          {item.quantity}
                        </Text>
                        <ActionIcon
                          variant="light"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        >
                          <IconPlus size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          onClick={() => removeItem(item.product.id)}
                          ml="auto"
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Group>
                    </div>
                  </Group>
                  <Divider my="md" />
                </div>
              ))}
            </ScrollArea>

            <div>
              <Group justify="space-between" mb="md">
                <Text size="lg" fw={700}>
                  {t('cart.total')}: {getTotal().toFixed(2)} {t('common.pln')}
                </Text>
              </Group>
              <Group gap="sm">
                <Button
                  variant="light"
                  color="gray"
                  onClick={clearCart}
                  style={{ flex: 1 }}
                >
                  {t('cart.clearCart')}
                </Button>
                <Button
                  onClick={handleCheckout}
                  style={{ flex: 1 }}
                >
                  {t('cart.checkout')}
                </Button>
              </Group>
            </div>
          </Stack>
        )}
      </Modal>
    </>
  );
};

